/**
 * Trainer Profile screen controller.
 * Trainer profile stored in cookies. Sprites resolved from local /assets/sprites/.
 */

import type { ScreenId } from "./home";
import type { TrainerProfile } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, dropdown, toast } from "../components";
import { TRAINER_PRESETS, DEFAULT_TRAINER_ID } from "../../data/assets";
import { getCookie, setCookie } from "../utils/cookies";
import { COOKIE_KEYS, SPRITE_LIMITS } from "../../config/constants";
import { downloadJson, pickFile, readFileAsText } from "../../data/backup";
import { trainerFrontUrl, trainerBackUrl, trainerVsUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";
import { exportTrainerCard, type CardFormat } from "../utils/trainer-card";

export function loadTrainerProfile(): TrainerProfile {
  const saved = getCookie<TrainerProfile | null>(COOKIE_KEYS.TRAINER, null);
  if (saved) return saved;
  return getDefaultTrainerProfile();
}

export function saveTrainerProfile(profile: TrainerProfile): void {
  setCookie(COOKIE_KEYS.TRAINER, profile);
}

export function getDefaultTrainerProfile(): TrainerProfile {
  return {
    name: "Trainer",
    backSprite: DEFAULT_TRAINER_ID,
    frontSprite: DEFAULT_TRAINER_ID,
    vsSprite: DEFAULT_TRAINER_ID,
    isCustom: false,
  };
}

export function renderTrainer(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: "Trainer Profile" }));
  header.appendChild(button("Back to Home", () => navigate("home"), { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main trainer-main" });
  let profile = loadTrainerProfile();

  // Display name
  const nameField = el("div", { className: "field" });
  nameField.appendChild(el("label", { text: "Display Name", className: "field-label" }));
  const nameInput = el("input", {
    className: "text-input",
    attrs: { type: "text", value: profile.name, placeholder: "Your name", maxlength: "20" },
  }) as HTMLInputElement;
  nameInput.addEventListener("input", () => { profile.name = nameInput.value; });
  nameField.appendChild(nameInput);
  main.appendChild(nameField);

  // Preset selector
  const presetOptions = TRAINER_PRESETS.map((t) => ({ value: t.id, label: t.name }));
  presetOptions.push({ value: "custom", label: "Custom Upload" });
  const selectedPresetId = profile.isCustom ? "custom" : (profile.frontSprite || DEFAULT_TRAINER_ID);

  const uploadSection = el("section", { className: "upload-section" });

  // Preview grid — refreshable
  const previewGrid = el("div", { className: "sprite-preview-grid" });
  function refreshPreviews(): void {
    clearChildren(previewGrid);
    previewGrid.appendChild(createSpritePreview("Front", profile.frontSprite, trainerFrontUrl));
    previewGrid.appendChild(createSpritePreview("Back", profile.backSprite, trainerBackUrl));
    previewGrid.appendChild(createSpritePreview("VS", profile.vsSprite, trainerVsUrl));
  }

  const presetDd = dropdown("Trainer Sprite Set", presetOptions, selectedPresetId, (val) => {
    if (val === "custom") {
      uploadSection.style.display = "block";
    } else {
      profile.backSprite = val;
      profile.frontSprite = val;
      profile.vsSprite = val;
      profile.isCustom = false;
      uploadSection.style.display = "none";
      refreshPreviews();
    }
  });
  main.appendChild(presetDd.container);

  // Custom upload section
  uploadSection.style.display = profile.isCustom ? "block" : "none";
  uploadSection.appendChild(el("h2", { text: "Custom Sprites" }));
  uploadSection.appendChild(el("p", {
    text: "Upload custom sprites. Front/back max 96x96px, VS max 128x128px.",
    className: "upload-hint",
  }));
  uploadSection.appendChild(createSpriteUploader("Front Sprite (opponent sees)", SPRITE_LIMITS.TRAINER_FRONT, (url) => {
    profile.frontSprite = url; profile.isCustom = true; refreshPreviews();
  }).container);
  uploadSection.appendChild(createSpriteUploader("Back Sprite (your view)", SPRITE_LIMITS.TRAINER_BACK, (url) => {
    profile.backSprite = url; profile.isCustom = true; refreshPreviews();
  }).container);
  uploadSection.appendChild(createSpriteUploader("VS Sprite", SPRITE_LIMITS.VS, (url) => {
    profile.vsSprite = url; profile.isCustom = true; refreshPreviews();
  }).container);
  main.appendChild(uploadSection);

  // Preview
  const previewSection = el("section", { className: "preview-section" });
  previewSection.appendChild(el("h2", { text: "Preview" }));
  refreshPreviews();
  previewSection.appendChild(previewGrid);
  main.appendChild(previewSection);

  // Actions
  const actionsBar = el("div", { className: "actions-bar" });
  actionsBar.appendChild(button("Save Profile", () => {
    saveTrainerProfile(profile);
    toast("Trainer profile saved!", "success");
  }));
  actionsBar.appendChild(button("Export Profile", () => {
    downloadJson(profile, "trainer-profile.json");
  }, { variant: "secondary" }));
  actionsBar.appendChild(button("Import Profile", async () => {
    const file = await pickFile("application/json");
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      profile = JSON.parse(text) as TrainerProfile;
      saveTrainerProfile(profile);
      toast("Profile imported!", "success");
      renderTrainer(container, navigate);
    } catch {
      toast("Failed to import profile", "error");
    }
  }, { variant: "secondary" }));
  actionsBar.appendChild(button("Reset to Default", () => {
    profile = getDefaultTrainerProfile();
    saveTrainerProfile(profile);
    toast("Profile reset to default", "info");
    renderTrainer(container, navigate);
  }, { variant: "danger" }));

  // Trainer card export
  const exportBar = el("div", { className: "trainer-card-export" });
  exportBar.appendChild(el("label", { text: "Trainer Card Export", className: "field-label" }));
  const formatSelect = el("select", { attrs: { "aria-label": "Card format" } }) as HTMLSelectElement;
  for (const opt of [{ value: "static", label: "Static PNG" }, { value: "animated", label: "Animated APNG" }]) {
    formatSelect.appendChild(el("option", { text: opt.label, attrs: { value: opt.value } }));
  }
  formatSelect.value = "animated";
  exportBar.appendChild(formatSelect);
  exportBar.appendChild(button("Export Trainer Card", async () => {
    try {
      await exportTrainerCard(profile, formatSelect.value as CardFormat);
    } catch (err) {
      toast("Failed to export trainer card", "error");
      console.error(err);
    }
  }));
  main.appendChild(exportBar);

  main.appendChild(actionsBar);
  container.appendChild(main);
}

function createSpriteUploader(
  label: string,
  maxSize: number,
  onUpload: (dataUrl: string) => void,
): { container: HTMLElement } {
  const container = el("div", { className: "field upload-field" });
  container.appendChild(el("label", { text: label, className: "field-label" }));
  const input = el("input", { attrs: { type: "file", accept: "image/png,image/gif,image/jpeg" } }) as HTMLInputElement;
  container.appendChild(input);
  const previewImg = el("img", { className: "sprite-upload-preview", attrs: { alt: "Preview" } }) as HTMLImageElement;
  previewImg.style.imageRendering = "pixelated";
  previewImg.style.display = "none";
  container.appendChild(previewImg);

  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const img = await loadImage(file);
      if (img.width > maxSize || img.height > maxSize) {
        toast(`Image too large. Max ${maxSize}x${maxSize}px. Yours: ${img.width}x${img.height}`, "error", 5000);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        onUpload(dataUrl);
        previewImg.src = dataUrl;
        previewImg.style.display = "block";
        toast(`${label} uploaded!`, "success");
      };
      reader.readAsDataURL(file);
    } catch {
      toast("Failed to load image", "error");
    }
  });
  return { container };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

function createSpritePreview(
  label: string,
  src: string,
  urlFn?: (id: string) => string,
): HTMLElement {
  const cell = el("div", { className: "sprite-preview-cell" });
  cell.appendChild(el("span", { text: label, className: "sprite-preview-label" }));
  const img = el("img", { className: "sprite-preview-img", attrs: { alt: label } }) as HTMLImageElement;
  img.style.imageRendering = "pixelated";
  cell.appendChild(img);

  if (src.startsWith("data:") || src.startsWith("blob:") || src.startsWith("http")) {
    img.src = src;
  } else if (src && urlFn) {
    const url = urlFn(src);
    void resolveSpriteUrl(url).then((resolved) => {
      if (resolved) img.src = resolved;
      else cell.appendChild(el("span", { text: "Not yet added", className: "sprite-preview-unavailable" }));
    });
  } else {
    cell.appendChild(el("span", { text: "Not set", className: "sprite-preview-unavailable" }));
  }
  return cell;
}